import { useRef, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../shadcn/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../shadcn/Dialog";
import { Brand } from "./Brand";
import { PageFooter } from "./Page";
import { BottomMenu } from "./BottomMenu";
import { cn } from "@/shared/utils/cn";

interface FullScreenDialogProps {
  open?: boolean;
  onClose: (reason?: DialogDismissReason) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  navigation?: ReactNode;
  narrowFooter?: boolean;
  contentClassName?: string;
  dismissible?: boolean;
}

export type DialogDismissReason = "dismiss" | "navigate";

export function FullScreenDialog({
  open = true,
  onClose,
  title,
  description,
  children,
  footer,
  navigation,
  narrowFooter = false,
  contentClassName,
  dismissible = true,
}: FullScreenDialogProps) {
  const previousFocus = useRef<HTMLElement | null>(null);
  const navigating = useRef(false);
  const { businessId, locationId } = useParams();
  const navigationContent = navigation !== undefined
    ? navigation
    : businessId && locationId ? <BottomMenu /> : null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && dismissible) onClose("dismiss"); }}>
      <DialogContent
        size="fullscreen"
        showCloseButton={false}
        onOpenAutoFocus={() => {
          navigating.current = false;
          previousFocus.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        }}
        onCloseAutoFocus={(event) => {
          // Pickers are opened programmatically, without a Radix DialogTrigger.
          if (navigating.current) {
            event.preventDefault();
            document.getElementById("main-content")?.focus();
          } else if (previousFocus.current?.isConnected) {
            event.preventDefault();
            previousFocus.current.focus();
          }
        }}
      >
        <div className="mx-auto flex min-h-16 w-full max-w-[960px] shrink-0 items-center justify-between gap-2 px-4 py-2 text-foreground sm:px-[30px]">
          <Brand />
          <DialogTitle className="min-w-0 flex-1 pr-0 text-xl font-extrabold">{title}</DialogTitle>
          {!navigationContent && <DialogClose asChild>
            <Button variant="quiet" size="sm" disabled={!dismissible}>Close</Button>
          </DialogClose>}
        </div>
        <DialogDescription className={description ? "mx-auto w-full max-w-[960px] px-4 pb-3 text-sm text-muted-foreground sm:px-[30px]" : "sr-only"}>
          {description || `${title} dialog`}
        </DialogDescription>
        <div className={cn("mx-auto min-h-0 w-full max-w-[960px] flex-1 overflow-y-auto overscroll-contain", contentClassName)}>{children}</div>
        {footer && (
          <div className={navigationContent ? "shrink-0 bg-card" : "shrink-0 bg-card pb-[env(safe-area-inset-bottom)]"}>
            <PageFooter narrow={narrowFooter} contentClassName={narrowFooter ? "max-w-[420px]" : undefined}>{footer}</PageFooter>
          </div>
        )}
        {navigationContent && (
          <div
            className="shrink-0"
            onClickCapture={(event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              if (!(event.target instanceof Element) || !event.target.closest("a[href]")) return;
              navigating.current = true;
              onClose("navigate");
            }}
          >
            {navigationContent}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
